/**
 * Performance Monitoring - Monitoramento de Core Web Vitals e performance
 */

import React from 'react'

// Thresholds para Core Web Vitals (baseados no Google)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint
  FID: { good: 100, poor: 300 },   // First Input Delay
  CLS: { good: 0.1, poor: 0.25 },  // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint
  TTFB: { good: 800, poor: 1800 }  // Time to First Byte
}

// Armazenar métricas coletadas
const performanceMetrics = {}

/**
 * Obtém rating baseado no threshold
 * @param {string} metricName - Nome da métrica
 * @param {number} value - Valor da métrica
 * @returns {string} Rating: 'good', 'needs-improvement', 'poor'
 */
const getRating = (metricName, value) => {
  const threshold = THRESHOLDS[metricName]
  if (!threshold) return 'unknown'
  
  if (value <= threshold.good) return 'good'
  if (value <= threshold.poor) return 'needs-improvement'
  return 'poor'
}

/**
 * Formatar valor da métrica para exibição
 * @param {string} metricName - Nome da métrica
 * @param {number} value - Valor da métrica
 * @returns {string} Valor formatado
 */
const formatMetricValue = (metricName, value) => {
  switch (metricName) {
    case 'CLS':
      return value.toFixed(3)
    case 'LCP':
    case 'FID':
    case 'FCP':
    case 'TTFB':
      return `${Math.round(value)}ms`
    default:
      return value.toString()
  }
}

/**
 * Callback para processar métricas do Web Vitals
 * @param {Object} metric - Objeto da métrica
 */
const handleMetric = (metric) => {
  const { name, value, rating, delta, id } = metric
  
  performanceMetrics[name] = {
    name,
    value,
    rating,
    delta,
    id,
    timestamp: Date.now(),
    formattedValue: formatMetricValue(name, value)
  }

  // Log no console para debug
  console.log(`Performance Metric - ${name}:`, {
    value: formatMetricValue(name, value),
    rating,
    threshold: THRESHOLDS[name]
  })

  // Enviar para analytics se configurado
  if (typeof gtag !== 'undefined') {
    gtag('event', name, {
      event_category: 'Web Vitals',
      event_label: id,
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      custom_map: { metric_rating: rating }
    })
  }

  // Trigger evento customizado para outros listeners
  window.dispatchEvent(new CustomEvent('performance-metric', {
    detail: performanceMetrics[name]
  }))
}

/**
 * Inicializar monitoramento de Web Vitals
 * Requer que a biblioteca web-vitals seja carregada
 */
export const initPerformanceMonitoring = async () => {
  try {
    // Verificar se web-vitals está disponível
    if (typeof window === 'undefined') return

    // Carregar web-vitals dinamicamente
    const { getCLS, getFID, getFCP, getLCP, getTTFB } = await import('web-vitals')

    // Configurar listeners para cada métrica
    getCLS(handleMetric)
    getFID(handleMetric)
    getFCP(handleMetric)
    getLCP(handleMetric)
    getTTFB(handleMetric)

    console.log('Performance monitoring initialized')
  } catch (error) {
    console.warn('Failed to initialize performance monitoring:', error)
  }
}

/**
 * Obter todas as métricas coletadas
 * @returns {Object} Objeto com todas as métricas
 */
export const getPerformanceMetrics = () => {
  return { ...performanceMetrics }
}

/**
 * Obter métrica específica
 * @param {string} metricName - Nome da métrica (LCP, FID, CLS, etc.)
 * @returns {Object|null} Dados da métrica ou null se não encontrada
 */
export const getMetric = (metricName) => {
  return performanceMetrics[metricName] || null
}

/**
 * Obter resumo das métricas de Core Web Vitals
 * @returns {Object} Resumo com LCP, FID e CLS
 */
export const getCoreWebVitals = () => {
  return {
    LCP: performanceMetrics.LCP || null,
    FID: performanceMetrics.FID || null,
    CLS: performanceMetrics.CLS || null
  }
}

/**
 * Verificar se Core Web Vitals estão dentro dos thresholds "good"
 * @returns {Object} Status de cada métrica e overall
 */
export const getCoreWebVitalsStatus = () => {
  const vitals = getCoreWebVitals()
  const status = {}
  let allGood = true

  Object.entries(vitals).forEach(([name, metric]) => {
    if (metric) {
      status[name] = {
        rating: metric.rating,
        isGood: metric.rating === 'good',
        value: metric.formattedValue
      }
      if (metric.rating !== 'good') allGood = false
    } else {
      status[name] = {
        rating: 'unknown',
        isGood: false,
        value: 'N/A'
      }
      allGood = false
    }
  })

  status.overall = {
    allGood,
    score: Object.values(status).filter(s => s.isGood).length,
    total: 3
  }

  return status
}

/**
 * Medir performance customizada
 * @param {string} name - Nome da medição
 * @param {Function} fn - Função a ser medida
 * @returns {Promise<any>} Resultado da função
 */
export const measurePerformance = async (name, fn) => {
  const startTime = performance.now()
  
  try {
    const result = await fn()
    const endTime = performance.now()
    const duration = endTime - startTime

    console.log(`Performance: ${name} took ${duration.toFixed(2)}ms`)

    // Registrar métrica customizada
    if (typeof gtag !== 'undefined') {
      gtag('event', 'timing_complete', {
        name,
        value: Math.round(duration)
      })
    }

    return result
  } catch (error) {
    const endTime = performance.now()
    const duration = endTime - startTime
    
    console.error(`Performance: ${name} failed after ${duration.toFixed(2)}ms`, error)
    throw error
  }
}

/**
 * Obter informações sobre a conexão de rede
 * @returns {Object} Informações da conexão
 */
export const getNetworkInfo = () => {
  if (typeof navigator === 'undefined' || !navigator.connection) {
    return { supported: false }
  }

  const connection = navigator.connection

  return {
    supported: true,
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    downlinkMax: connection.downlinkMax,
    rtt: connection.rtt,
    saveData: connection.saveData,
    type: connection.type
  }
}

/**
 * Obter informações sobre a performance da página
 * @returns {Object} Métricas de timing da página
 */
export const getPageTimings = () => {
  if (typeof performance === 'undefined' || !performance.timing) {
    return { supported: false }
  }

  const timing = performance.timing
  const navigation = performance.navigation

  return {
    supported: true,
    // Tempos de carregamento básicos
    domainLookup: timing.domainLookupEnd - timing.domainLookupStart,
    connect: timing.connectEnd - timing.connectStart,
    request: timing.responseStart - timing.requestStart,
    response: timing.responseEnd - timing.responseStart,
    domProcessing: timing.domComplete - timing.domLoading,
    
    // Métricas principais
    ttfb: timing.responseStart - timing.navigationStart,
    domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
    loadComplete: timing.loadEventEnd - timing.navigationStart,
    
    // Informações de navegação
    navigationType: navigation.type,
    redirectCount: navigation.redirectCount
  }
}

/**
 * Hook React para usar métricas de performance
 * @returns {Object} Estado das métricas e funções utilitárias
 */
export const usePerformanceMetrics = () => {
  const [metrics, setMetrics] = React.useState(performanceMetrics)
  const [isSupported, setIsSupported] = React.useState(false)

  React.useEffect(() => {
    setIsSupported(typeof performance !== 'undefined')

    const handleMetricUpdate = (event) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        [event.detail.name]: event.detail
      }))
    }

    window.addEventListener('performance-metric', handleMetricUpdate)
    
    return () => {
      window.removeEventListener('performance-metric', handleMetricUpdate)
    }
  }, [])

  return {
    metrics,
    isSupported,
    getCoreWebVitals: () => getCoreWebVitals(),
    getStatus: () => getCoreWebVitalsStatus(),
    getNetworkInfo: () => getNetworkInfo(),
    getPageTimings: () => getPageTimings()
  }
}

// Exportar constantes úteis
export { THRESHOLDS, getRating, formatMetricValue }
